import { useState } from 'react'
import AdvancedComboBox, { AdvancedComboBoxProps } from 'components/AdvancedComboBox'
import { StorePart, useStorePartDetailsQuery } from 'graphql'
import useStorePartsSearch from 'hooks/storePartsSearch'
import { useStorePartAutoPopulate } from './useStorePartAutoPopulate'

export type StorePartPickerProps = {
  id?: string,
  debounceKey?: string,
	isDisabled?: boolean,
  label?: string,
	storeId: number,
  onChange: ( id?: number, option?: StorePart ) => void,
  onNotFound?: ( productId: number ) => void,
  selectedPart?: StorePart,
  selectedProductId?: number,
  required?: boolean,
} & Partial<Omit<AdvancedComboBoxProps<StorePart>, 'onChange'>>

export default function StorePartPicker( { storeId, debounceKey, selectedPart, selectedProductId, onChange, onNotFound, ...props }: StorePartPickerProps ): JSX.Element {
	const [ searchString, setSearchString ] = useState( '' )
	const { loading, data } = useStorePartsSearch( storeId, searchString, debounceKey )

	// If productId provided, fetch the StorePart for this product at this store
	const { data: storePartData, loading: storePartLoading, called } = useStorePartDetailsQuery( {
		variables: { storeId, partId: selectedProductId },
		skip: !selectedProductId || !!selectedPart || !storeId,
	} )

	// Auto-populate the form when StorePart data is fetched or call onNotFound if not found
	useStorePartAutoPopulate( {
		called,
		storePartLoading,
		storePartData,
		selectedProductId,
		selectedPart,
		onChange,
		onNotFound,
	} )

	const effectiveSelectedPart = selectedPart ?? ( storePartData?.storePart ? storePartData.storePart as StorePart : undefined )

	return (
		<AdvancedComboBox
			setSelectedOption={( o?: StorePart ) => onChange( o?.id, o )}
			selectedOption={effectiveSelectedPart}
			data={data?.storeParts as unknown as StorePart[]}
			getName={( { part }: StorePart ) => `${part.partNumber} - ${part.partClass.description}`}
			getSecondaryText={( { part }: StorePart ) => part.provider.name}
			setSearchString={setSearchString}
			isLoading={loading || storePartLoading}
			{...props}
		/>
	)
}
